import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { SettingsOutlined, WarningAmber } from "@mui/icons-material";
import {
  GridApiContext,
  GridColDef,
  GridRowId,
} from "@mui/x-data-grid-premium";
import { SelectOption } from "../types";
import { ExtendedGridColDef } from "../overrides";
import { aliasRecordKeys } from "../fieldAliases";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EditFormModalProps {
  open: boolean;
  onClose: () => void;
  columns: GridColDef[];
  selectedRows: any[];
  tableName: string;
  apiRef: React.RefObject<any>;
  onSingleUpdate: (
    rowId: GridRowId,
    data: Record<string, any>
  ) => Promise<{ success: boolean; error?: string }>;
  onBulkSet: (
    ids: (string | number)[],
    fields: Record<string, any>
  ) => Promise<{ success: boolean; error?: string }>;
  fieldOptions?: Record<string, SelectOption[]>;
  getRowId: (row: any) => string | number;
  defaultFieldVisibility?: Record<string, boolean>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getLocalStorageKey(tableName: string) {
  return `sdg-edit-visibility-${tableName}`;
}

function loadVisibility(
  tableName: string,
  editableFields: GridColDef[],
  defaultFieldVisibility?: Record<string, boolean>
): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(getLocalStorageKey(tableName));
    // Saved preferences may use renamed field ids
    if (raw) return aliasRecordKeys(JSON.parse(raw));
  } catch {
    // ignore parse errors
  }
  if (defaultFieldVisibility) {
    const result: Record<string, boolean> = {};
    editableFields.forEach((col) => {
      result[col.field] = defaultFieldVisibility[col.field] === true;
    });
    return result;
  }
  const defaults: Record<string, boolean> = {};
  editableFields.forEach((col) => {
    defaults[col.field] = true;
  });
  return defaults;
}

function saveVisibility(
  tableName: string,
  visibility: Record<string, boolean>
) {
  try {
    localStorage.setItem(
      getLocalStorageKey(tableName),
      JSON.stringify(visibility)
    );
  } catch (e) {
    console.log(e);
  }
}

function resolveValueOptions(
  col: GridColDef,
  fieldOptions?: Record<string, SelectOption[]>
): SelectOption[] {
  if (fieldOptions?.[col.field]?.length) return fieldOptions[col.field];
  const vo = (col as any).valueOptions;
  if (!vo || typeof vo === "function") return [];
  return (vo as any[]).map((opt) =>
    typeof opt === "object" && opt !== null
      ? { value: opt.value, label: String(opt.label ?? opt.value) }
      : { value: opt, label: String(opt) }
  );
}

function findIdFieldName(row: any, idValue: string | number): string {
  return Object.keys(row).find((k) => row[k] === idValue) ?? "id";
}

/**
 * Access a field value from a row using dot-notation field paths.
 * Handles both flat storage  ("supply.title" → row["supply.title"])
 * and nested storage ("supply.title" → row.supply.title).
 */
function getFieldValue(row: any, fieldPath: string): any {
  // Try the flat key first — this is the most common case in this codebase
  if (Object.prototype.hasOwnProperty.call(row, fieldPath)) {
    return row[fieldPath];
  }
  // Fall back to nested traversal for grids that store rows as nested objects
  const parts = fieldPath.split(".");
  let current = row;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = current[part];
  }
  return current;
}

// ─── Mixed-values overlay ─────────────────────────────────────────────────────
// Shown in place of the real input when multi-row rows disagree on a field value.
// Clicking it removes it and reveals an empty input ready to type in.

interface MixedOverlayProps {
  label: string;
  disabled: boolean;
  onUnlock: () => void;
}

function MixedValuesOverlay({ label, disabled, onUnlock }: MixedOverlayProps) {
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onUnlock()}
      onKeyDown={(e) => !disabled && e.key === "Enter" && onUnlock()}
      aria-label={`${label}: mixed values — click to edit`}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1,
        border: "1px solid",
        borderColor: "warning.main",
        borderRadius: "4px",
        px: "14px",
        // match MUI small TextField height
        py: "8.5px",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
        "&:hover:not([aria-disabled='true'])": {
          borderColor: "warning.dark",
          bgcolor: "warning.50",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "warning.main",
          outlineOffset: 1,
        },
      }}
    >
      {/* Floating label — mimics MUI shrunk InputLabel */}
      <Typography
        component="span"
        sx={{
          position: "absolute",
          top: "-9px",
          left: "8px",
          px: "4px",
          bgcolor: "background.paper",
          color: "warning.main",
          fontSize: "0.75rem",
          lineHeight: 1.2,
          pointerEvents: "none",
        }}
      >
        {label}
      </Typography>

      <WarningAmber
        fontSize="small"
        sx={{ color: "warning.main", flexShrink: 0 }}
      />
      <Typography
        variant="body2"
        sx={{ color: "warning.dark", fontStyle: "italic" }}
      >
        Mixed values
      </Typography>
    </Box>
  );
}

// ─── Field renderer ───────────────────────────────────────────────────────────

interface FieldProps {
  col: GridColDef;
  value: any;
  isMixed: boolean;
  disabled: boolean;
  fieldOptions?: Record<string, SelectOption[]>;
  onChange: (field: string, value: any) => void;
  onUnlock: (field: string) => void;
}

/**
 * Renders a custom-type column's renderEditCell outside the DataGrid by
 * providing a minimal mock grid API context. The mock intercepts
 * setEditCellValue and routes the new value through the form's onChange.
 *
 * OptionsContext is still provided by the ancestor ServerDataGrid, so
 * components like MultiSelectEditCell that call useFieldOptions() work as-is.
 */
function CustomEditCellField({
  col,
  value,
  isMixed,
  disabled,
  onChange,
  onUnlock,
}: FieldProps) {
  const label = (col as any).headerName ?? col.field;

  // Stable mock apiRef — setEditCellValue routes value changes into form state.
  // onChange is stable (useCallback with no deps) so the closure never goes stale.
  const mockApiRef = useRef<any>({
    setEditCellValue: ({
      field,
      value: newValue,
    }: {
      id: any;
      field: string;
      value: any;
    }) => {
      onChange(field, newValue);
    },
  });

  if (isMixed) {
    return (
      <MixedValuesOverlay
        label={label}
        disabled={disabled}
        onUnlock={() => onUnlock(col.field)}
      />
    );
  }

  const params = {
    id: "edit-form-row" as GridRowId,
    field: col.field,
    value,
    row: {},
    rowNode: {} as any,
    colDef: col as any,
    cellMode: "edit" as const,
    isEditable: !disabled,
    tabIndex: 0 as const,
    hasFocus: false,
    formattedValue: value,
    api: mockApiRef.current,
  };

  return (
    <GridApiContext.Provider value={mockApiRef}>
      <Box sx={disabled ? { pointerEvents: "none", opacity: 0.5 } : undefined}>
        <Typography
          component="label"
          sx={{
            display: "block",
            fontSize: "0.75rem",
            color: "text.secondary",
            mb: 0.5,
            lineHeight: 1.4,
          }}
        >
          {label}
        </Typography>
        {col.renderEditCell!(params as any)}
      </Box>
    </GridApiContext.Provider>
  );
}

function EditField({
  col,
  value,
  isMixed,
  disabled,
  fieldOptions,
  onChange,
  onUnlock,
}: FieldProps) {
  const label = (col as any).headerName ?? col.field;
  const type = col.type;

  // Custom-type columns supply their own renderEditCell; render it via the
  // context bridge so useGridApiContext() works outside the DataGrid.
  if (type === "custom" && col.renderEditCell) {
    return (
      <CustomEditCellField
        col={col}
        value={value}
        isMixed={isMixed}
        disabled={disabled}
        fieldOptions={fieldOptions}
        onChange={onChange}
        onUnlock={onUnlock}
      />
    );
  }

  // Always show the mixed-values overlay until the user explicitly unlocks it
  if (isMixed) {
    return (
      <MixedValuesOverlay
        label={label}
        disabled={disabled}
        onUnlock={() => onUnlock(col.field)}
      />
    );
  }

  if (type === "boolean") {
    return (
      <TextField
        fullWidth
        select
        label={label}
        value={value === true || value === "true" ? "true" : "false"}
        onChange={(e) =>
          onChange(col.field, e.target.value === "true" ? true : false)
        }
        disabled={disabled}
        size="small"
      >
        <MenuItem value="false">No</MenuItem>
        <MenuItem value="true">Yes</MenuItem>
      </TextField>
    );
  }

  if (type === "singleSelect") {
    const options = resolveValueOptions(col, fieldOptions);
    return (
      <TextField
        fullWidth
        select
        label={label}
        value={value ?? ""}
        onChange={(e) => onChange(col.field, e.target.value)}
        disabled={disabled}
        size="small"
      >
        {options.map((opt) => (
          <MenuItem key={String(opt.value)} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (type === "number") {
    return (
      <TextField
        fullWidth
        label={label}
        type="number"
        value={value ?? ""}
        onChange={(e) =>
          onChange(
            col.field,
            e.target.value !== "" ? Number(e.target.value) : null
          )
        }
        disabled={disabled}
        size="small"
      />
    );
  }

  if (type === "date") {
    return (
      <TextField
        fullWidth
        label={label}
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange(col.field, e.target.value)}
        disabled={disabled}
        size="small"
        slotProps={{ inputLabel: { shrink: true } }}
      />
    );
  }

  if (type === "dateTime") {
    return (
      <TextField
        fullWidth
        label={label}
        type="datetime-local"
        value={value ?? ""}
        onChange={(e) => onChange(col.field, e.target.value)}
        disabled={disabled}
        size="small"
        slotProps={{ inputLabel: { shrink: true } }}
      />
    );
  }

  // Default: string / text
  return (
    <TextField
      fullWidth
      label={label}
      value={value ?? ""}
      onChange={(e) => onChange(col.field, e.target.value)}
      disabled={disabled}
      size="small"
    />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function EditFormModal({
  open,
  onClose,
  columns,
  selectedRows,
  tableName,
  apiRef,
  onSingleUpdate,
  onBulkSet,
  fieldOptions,
  getRowId,
  defaultFieldVisibility,
}: EditFormModalProps) {
  const editableColumns = columns.filter(
    (col) =>
      col.editable &&
      col.type !== "actions" &&
      (col as ExtendedGridColDef).editType !== "inline"
  );

  const [formValues, setFormValues] = useState<Record<string, any>>({});
  // Fields whose rows disagree — show Mixed overlay until user clicks to unlock
  const [mixedFields, setMixedFields] = useState<Set<string>>(new Set());
  // Fields the user has actually typed a new value into
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState<
    Record<string, boolean>
  >(() => loadVisibility(tableName, editableColumns, defaultFieldVisibility));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-load visibility preferences when tableName changes
  useEffect(() => {
    setFieldVisibility(
      loadVisibility(tableName, editableColumns, defaultFieldVisibility)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName]);

  // Initialize form values when the modal opens
  useEffect(() => {
    if (!open || selectedRows.length === 0) return;

    const initialValues: Record<string, any> = {};
    const newMixed = new Set<string>();

    editableColumns.forEach((col) => {
      if (selectedRows.length === 1) {
        // Single row — pre-load the existing value
        initialValues[col.field] =
          getFieldValue(selectedRows[0], col.field) ?? "";
      } else {
        const firstVal = getFieldValue(selectedRows[0], col.field);
        const allSame = selectedRows.every((row) => {
          const val = getFieldValue(row, col.field);
          if (Array.isArray(firstVal) && Array.isArray(val)) {
            if (firstVal.length !== val.length) return false;
            const toKey = (item: any) =>
              item && typeof item === "object" ? item.value : item;
            const a = [...firstVal].map(toKey).sort();
            const b = [...val].map(toKey).sort();
            return a.every((v, i) => v === b[i]);
          }
          return val === firstVal;
        });
        if (allSame) {
          // All rows share the same value — pre-load it
          initialValues[col.field] = firstVal ?? "";
        } else {
          // Rows disagree — lock this field behind Mixed overlay
          // Value is empty; it will remain empty if user unlocks and doesn't type
          initialValues[col.field] = "";
          newMixed.add(col.field);
        }
      }
    });

    setFormValues(initialValues);
    setMixedFields(newMixed);
    setTouchedFields(new Set());
    setSettingsOpen(false);
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Called when user clicks the Mixed-values overlay for a field.
  // Removes the overlay and exposes an empty input — user is responsible
  // for typing a value if they want to overwrite.
  const handleUnlockMixedField = useCallback((field: string) => {
    setMixedFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
    // Leave formValues[field] = "" and do NOT mark as touched.
    // Only actual keystrokes will add it to touchedFields.
  }, []);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setTouchedFields((prev) => new Set(prev).add(field));
    setError(null);
  }, []);

  const handleVisibilityToggle = useCallback(
    (field: string) => {
      setFieldVisibility((prev) => {
        const updated = { ...prev, [field]: !prev[field] };
        saveVisibility(tableName, updated);
        return updated;
      });
    },
    [tableName]
  );

  const handleResetVisibility = useCallback(() => {
    if (!defaultFieldVisibility) return;
    const reset: Record<string, boolean> = {};
    editableColumns.forEach((col) => {
      reset[col.field] = defaultFieldVisibility[col.field] === true;
    });
    saveVisibility(tableName, reset);
    setFieldVisibility(reset);
  }, [tableName, defaultFieldVisibility]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (touchedFields.size === 0) {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (selectedRows.length === 1) {
        const row = selectedRows[0];
        const idValue = getRowId(row);
        const idFieldName = findIdFieldName(row, idValue);

        const changedData: Record<string, any> = { [idFieldName]: idValue };
        touchedFields.forEach((field) => {
          changedData[field] = formValues[field];
        });

        const result = await onSingleUpdate(idValue, changedData);
        if (!result.success) throw new Error(result.error ?? "Update failed");

        apiRef.current?.dataSource?.fetchRows();
      } else {
        // Multi-row: only submit fields the user explicitly typed into.
        // Mixed fields that were never unlocked (or unlocked but not typed)
        // are NOT in touchedFields and will be skipped.
        const changedFields: Record<string, any> = {};
        touchedFields.forEach((field) => {
          changedFields[field] = formValues[field];
        });

        if (Object.keys(changedFields).length === 0) {
          onClose();
          return;
        }

        const ids = selectedRows.map((row) => getRowId(row));
        const result = await onBulkSet(ids, changedFields);
        if (!result.success)
          throw new Error(result.error ?? "Bulk update failed");

        apiRef.current?.dataSource?.fetchRows();
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const visibleEditableColumns = editableColumns.filter(
    (col) => fieldVisibility[col.field] !== false
  );

  const isMultiRow = selectedRows.length > 1;
  const lockedMixedCount = visibleEditableColumns.filter((col) =>
    mixedFields.has(col.field)
  ).length;

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <span>
          {settingsOpen
            ? "Field Visibility"
            : isMultiRow
            ? `Edit ${selectedRows.length} Rows`
            : "Edit Row"}
        </span>
        <Tooltip
          title={settingsOpen ? "Back to form" : "Field visibility settings"}
        >
          <IconButton
            size="small"
            onClick={() => setSettingsOpen((prev) => !prev)}
            color={settingsOpen ? "primary" : "default"}
          >
            <SettingsOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      {settingsOpen ? (
        <DialogContent>
          <Grid container spacing={0}>
            {editableColumns.map((col) => (
              <Grid size={{ xs: 12, sm: 6 }} key={col.field}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={fieldVisibility[col.field] !== false}
                      onChange={() => handleVisibilityToggle(col.field)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {(col as any).headerName ?? col.field}
                    </Typography>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      ) : (
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isMultiRow && lockedMixedCount > 0 && (
            <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
              {lockedMixedCount} field
              {lockedMixedCount !== 1 ? "s have" : " has"} different values
              across the selected rows. Click a field to unlock it and set a new
              value for all rows.
            </Alert>
          )}

          {visibleEditableColumns.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ py: 2, textAlign: "center" }}
            >
              No fields visible. Use the settings icon to show fields.
            </Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {visibleEditableColumns.map((col) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={col.field}>
                    <EditField
                      col={col}
                      value={formValues[col.field]}
                      isMixed={mixedFields.has(col.field)}
                      disabled={loading}
                      fieldOptions={fieldOptions}
                      onChange={handleFieldChange}
                      onUnlock={handleUnlockMixedField}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
      )}

      <DialogActions>
        {settingsOpen ? (
          <>
            {defaultFieldVisibility && (
              <Button onClick={handleResetVisibility} color="warning">
                Reset to default
              </Button>
            )}
            <Button onClick={() => setSettingsOpen(false)}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading || visibleEditableColumns.length === 0}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
